import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EList, EListActiveEntry, EListItemEntry} from 'webapp-lib/Components';
import {mixinFlexboxColumn, mixinFlexboxRow} from 'webapp-lib/styling';

import {SimplePlaceholder} from '#/Placeholder/SimplePlaceholder';
import {I18NextNS} from '_/i18n';

import {PdfHighlight} from '../PdfHighlight.utils';

import {PdfHighlightItem, PdfHighlightPageHeader} from './PdfHighlightItem';

export const UPdfHighlightList = ({
  highlights,
  initialActiveHighlight,
  onActiveItemChange,
}: {
  highlights: PdfHighlight[];
  initialActiveHighlight?: EListActiveEntry;
  onActiveItemChange?: (
    activeItem: PdfHighlight | undefined,
    highlightIndex: number
  ) => void;
}): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('common');
  const [activeEntry, setActiveEntry] = useState<EListActiveEntry>(
    initialActiveHighlight ?? '0'
  );

  const searchEntities = highlights.map(
    (item, index): EListItemEntry<PdfHighlight> => ({
      id: String(index),
      raw: item,
      Component: PdfHighlightItem,
      stickyHeaderId: `page-header-${item.pageIndex}`,
      stickyHeaderComponent: PdfHighlightPageHeader,
      options: {
        singleActions: [],
      },
    })
  );

  const handleActiveEntityChange = (entry: EListActiveEntry | undefined) => {
    if (entry && typeof entry !== 'string') {
      throw new Error(
        `Unexpected "entityId" value for active entity: ${JSON.stringify(entry)}`
      );
    }
    if (entry) {
      setActiveEntry(entry);
      const index = Number(entry);
      onActiveItemChange?.(highlights[index], index);
    }
  };

  if (highlights.length === 0) {
    return (
      <ListWrapper>
        <SimplePlaceholder customText={t('pdfViewer.highlights.noHits')} />
      </ListWrapper>
    );
  }

  return (
    <ListWrapper>
      <StyledTopRow>
        <StyledSearchHits>
          {t('pdfViewer.highlights.hits', {hits: highlights.length})}
        </StyledSearchHits>
      </StyledTopRow>
      <EList
        activeItem={activeEntry}
        data={searchEntities}
        testId='pdfHighlights'
        onActiveItemChange={handleActiveEntityChange}
      />
    </ListWrapper>
  );
};

export const PdfHighlightList = observer(UPdfHighlightList);

const ListWrapper = styled.div`
  ${mixinFlexboxColumn};
  height: 100%;
  width: 100%;
  padding: var(--gutter-little) 0;
`;

const StyledTopRow = styled.div`
  ${mixinFlexboxRow};
  align-items: center;
  padding: var(--gutter-small);
  background: var(--color-core-cockpit-boxes);
  border-bottom: 2px solid var(--color-core-black);
`;

const StyledSearchHits = styled.span`
  font-size: var(--font-size-small);
`;
